/**
 * PopupRefreshIndicator - Shows a subtle loading indicator while data is refreshing
 *
 * This component displays a small, non-intrusive indicator when new data is being
 * fetched while existing data is still shown. It follows the "stale while revalidate"
 * pattern common in modern data fetching.
 *
 * @example
 * <PopupRefreshIndicator isRefreshing={isRefreshing} />
 *
 * @example
 * // With custom message
 * <PopupRefreshIndicator
 *   isRefreshing={isRefreshing}
 *   message="Updating targets..."
 * />
 *
 * @example
 * // Inline variant for headers
 * <div className="flex items-center gap-2">
 *   <h1>Targets on Map</h1>
 *   <PopupRefreshIndicator isRefreshing={isRefreshing} variant="inline" />
 * </div>
 */
import React from 'react';
import { cn } from '@ais-ui/utils';
import { Loader2 } from 'lucide-react';

export interface PopupRefreshIndicatorProps {
  /** Whether data is currently being refreshed */
  isRefreshing: boolean;
  /** Optional custom message to display */
  message?: string;
  /** Display variant */
  variant?: 'default' | 'inline' | 'banner';
  /** Additional CSS classes */
  className?: string;
}

export const PopupRefreshIndicator: React.FC<PopupRefreshIndicatorProps> = ({
  isRefreshing,
  message = 'Updating...',
  variant = 'default',
  className,
}) => {
  if (!isRefreshing) return null;

  if (variant === 'inline') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in duration-200',
          className,
        )}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>{message}</span>
      </span>
    );
  }

  if (variant === 'banner') {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-2 py-2 px-4 bg-primary/10 text-primary text-sm font-medium',
          'border-b border-primary/20 animate-in slide-in-from-top duration-200',
          className,
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{message}</span>
      </div>
    );
  }

  // Default variant - overlay indicator at top center, doesn't shift layout
  return (
    <div
      className={cn(
        'fixed top-4 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-2 py-2.5 px-4 rounded-full',
        'bg-primary text-primary-foreground shadow-lg',
        'text-sm font-medium',
        'animate-in fade-in zoom-in-95 duration-200',
        className,
      )}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{message}</span>
    </div>
  );
};

export default PopupRefreshIndicator;
